/**
 * Cookie Storage Utility for Cross-Subdomain Authentication
 *
 * This module provides cookie-based storage that works across subdomains
 * by setting cookies on the parent domain (e.g., .evaluans.fr)
 *
 * Data is Base64 encoded for light obfuscation (not encryption).
 * For sensitive data, consider server-side encryption.
 */

/**
 * Encode a string to Base64 (UTF-8 safe)
 */
function encodeBase64(value: string): string {
  try {
    // Handle UTF-8 characters properly
    const utf8Value = encodeURIComponent(value).replace(
      /%([0-9A-F]{2})/g,
      (_, p1) => {
        return String.fromCharCode(parseInt(p1, 16));
      },
    );
    return btoa(utf8Value);
  } catch {
    console.error("Failed to encode Base64");
    return "";
  }
}

/**
 * Decode a Base64 string (UTF-8 safe)
 */
function decodeBase64(value: string): string | null {
  try {
    const decoded = atob(value);
    // Restore UTF-8 characters
    return decodeURIComponent(
      decoded
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
  } catch {
    console.error("Failed to decode Base64");
    return null;
  }
}

export interface CookieOptions {
  days?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
}

// Default cookie options - 24 hours expiry
const DEFAULT_OPTIONS: CookieOptions = {
  days: 1, // 24 hours
  path: "/",
  secure: true,
  sameSite: "Lax",
};

/**
 * Get the parent domain for cookie sharing across subdomains
 * In development (localhost), returns undefined to use default behavior
 */
function getCookieDomain(): string | undefined {
  // Check for explicit configuration via environment variable
  // Using BUN_PUBLIC_ prefix for client-side access
  const explicitDomain = process.env.BUN_PUBLIC_COOKIE_DOMAIN;
  if (explicitDomain) {
    return explicitDomain;
  }

  // In browser environment
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    // localhost - no domain sharing needed/possible
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return undefined;
    }

    // Extract parent domain from subdomain
    // e.g., ent-struct.evaluans.fr -> .evaluans.fr
    const parts = hostname.split(".");
    if (parts.length >= 2) {
      // Return parent domain with leading dot for subdomain sharing
      return `.${parts.slice(-2).join(".")}`;
    }
  }

  return undefined;
}

/**
 * Set a cookie with the given name, value, and options
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const domain = getCookieDomain();

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // Add expiry
  if (opts.days) {
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + opts.days * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${expiryDate.toUTCString()}`;
  }

  // Add path
  if (opts.path) {
    cookieString += `; path=${opts.path}`;
  }

  // Add domain for cross-subdomain sharing
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Add security flags
  if (opts.secure) {
    cookieString += "; secure";
  }

  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const nameEQ = encodeURIComponent(name) + "=";
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i]?.trim();
    if (cookie && cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }

  return null;
}

/**
 * Delete a cookie by name
 */
export function deleteCookie(name: string, options: CookieOptions = {}): void {
  const opts = { ...options, days: -1 };
  setCookie(name, "", opts);
}

/**
 * Check if cookies are enabled in the browser
 */
export function areCookiesEnabled(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  return navigator.cookieEnabled;
}

/**
 * Create a Zustand-compatible storage interface using cookies
 * This allows Zustand's persist middleware to use cookies instead of localStorage
 * Data is Base64 encoded for light obfuscation
 */
export function createCookieStorage<T = unknown>(
  storageKey: string,
  options: CookieOptions = {},
) {
  return {
    getItem: (): string | null => {
      const encodedValue = getCookie(storageKey);
      if (!encodedValue) {
        return null;
      }
      // Decode Base64
      const decoded = decodeBase64(encodedValue);
      return decoded;
    },
    setItem: (_name: string, value: string): void => {
      // Encode to Base64 before storing
      const encodedValue = encodeBase64(value);
      setCookie(storageKey, encodedValue, options);
    },
    removeItem: (): void => {
      deleteCookie(storageKey, options);
    },
  };
}

/**
 * Hybrid storage that uses cookies in production and localStorage in development
 * This provides the best of both worlds: cross-subdomain sharing in production
 * and easy development on localhost
 *
 * In production: cookies are Base64 encoded
 * In development: localStorage is used as-is (no encoding needed)
 */
export function createHybridStorage<T = unknown>(
  storageKey: string,
  options: CookieOptions = {},
) {
  const isDevelopment =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  if (isDevelopment) {
    // Use localStorage in development (no encoding needed for local dev)
    return {
      getItem: (): string | null => {
        const value = localStorage.getItem(storageKey);
        return value;
      },
      setItem: (_name: string, value: string): void => {
        localStorage.setItem(storageKey, value);
      },
      removeItem: (): void => {
        localStorage.removeItem(storageKey);
      },
    };
  }

  // Use cookies with Base64 encoding in production for cross-subdomain sharing
  return createCookieStorage<T>(storageKey, options);
}
